import { GrammarService, JokesService, VocabularyService } from '@shared/openapi/jaaluu-api';
import type {
  GrammerChapter,
  GrammerMenuRes,
  LanguageLevel
} from '@shared/openapi/jaaluu-api/types.gen';
import sharedStores, { animate } from '@shared/shared-stores';
import { ref, type Ref } from 'vue';
import { language } from '.';

const LAST_GRAMMAR_CHAPTER_KEY = 'lastGrammarChapter';
const LAST_VOCAB_INDEX_KEY = 'lastVocabularyIndex';
const LAST_JOKE_INDEX_KEY = 'lastJokeIndex';

export default () => {
  const { langForeign, langNative, langLevel } = language.state;
  const { addConfetti } = animate.funcs;
  const { notify } = sharedStores.snackbar.funcs;
  const grammarMenu = ref<GrammerMenuRes | null>(null);
  const grammarChapterStates: Ref<any> = ref({});
  const vocabularyIndexStates: Ref<Record<string, number>> = ref({});
  const vocabularyTotalStates: Ref<Record<string, number>> = ref({});
  const jokeIndexStates: Ref<Record<string, number>> = ref({});
  const jokeTotalStates: Ref<Record<string, number>> = ref({});
  const showGrammarDone = ref(false);
  const completedGrammarChapterNumber = ref('');

  const loadGrammarMenu = () => {
    if (!langForeign.value || !langLevel.value || !langNative.value) {
      console.error('Language or language level not set');
      return;
    }

    return GrammarService.getGrammarMenuV1GrammarMenuGet({
      query: {
        lang: langForeign.value,
        target_lang: langNative.value,
        lang_level: langLevel.value.replace('_', '-') as LanguageLevel
      }
    }).then((response) => {
      grammarMenu.value = response.data ? response.data : null;
    });
  };

  // Flatten all chapters into a sequential list
  const flattenChapters = (): string[] => {
    
    const result: string[] = [];

    const traverse = (chapterList: GrammerChapter[]) => {
      for (const chapter of chapterList) {
        result.push(chapter.chapter_number);
        if (chapter.sub_chapters.length > 0) {
          traverse(chapter.sub_chapters);
        }
      }
    };
    const chapters = grammarMenu.value ? grammarMenu.value.chapters : [];
    traverse(chapters);
    return result;
  };

  // Get the next chapter number
  const getNextChapterNumber = (currentChapter: string): string | null => {
    const flatList = flattenChapters();
    const currentIndex = flatList.indexOf(currentChapter);

    if (currentIndex === -1 || currentIndex === flatList.length - 1) {
      return null;
    }

    return flatList[currentIndex + 1];
  };

  // Get the previous chapter number
  const getPreviousChapterNumber = (currentChapter: string): string | null => {
    const flatList = flattenChapters();
    const currentIndex = flatList.indexOf(currentChapter);

    if (currentIndex <= 0) {
      return null;
    }

    return flatList[currentIndex - 1];
  };


  // Find a chapter by its number
  const findChapter = (
    chapterNumber: string,
    chapters: GrammerChapter[]
  ): GrammerChapter | null => {
    for (const chapter of chapters) {
      if (chapter.chapter_number === chapterNumber) {
        return chapter;
      }
      if (chapter.sub_chapters.length > 0) {
        const found = findChapter(chapterNumber, chapter.sub_chapters);
        if (found) return found;
      }
    }
    return null;
  };

  const setLastGrammarChapter = (chapterNumber: string) => {
    if (!langForeign.value || !langLevel.value) return;

    if (!grammarChapterStates.value[langForeign.value])
      grammarChapterStates.value[langForeign.value] = {};
    grammarChapterStates.value[langForeign.value][langLevel.value] = chapterNumber;

    localStorage.setItem(LAST_GRAMMAR_CHAPTER_KEY, JSON.stringify(grammarChapterStates.value));
  };

  const getLastGrammarChapter = (): string | undefined => {
    if (langForeign.value && langLevel.value) {
      return grammarChapterStates.value?.[langForeign.value]?.[langLevel.value];
    }
  };

  const initLastGrammarChapterState = () => {
    const stored = localStorage.getItem(LAST_GRAMMAR_CHAPTER_KEY);
    if (stored) {
      grammarChapterStates.value = JSON.parse(stored);
    }
  };

  const setLastVocabularyIndex = (index: number) => {
    if (!langForeign.value) return;
    vocabularyIndexStates.value[langForeign.value] = index;
    localStorage.setItem(LAST_VOCAB_INDEX_KEY, JSON.stringify(vocabularyIndexStates.value));
  };

  const getLastVocabularyIndex = (): number | undefined => {
    if (!langForeign.value) return undefined;
    return vocabularyIndexStates.value[langForeign.value];
  };

  const initLastVocabularyIndexState = () => {
    const stored = localStorage.getItem(LAST_VOCAB_INDEX_KEY);
    if (stored) {
      vocabularyIndexStates.value = JSON.parse(stored);
    }
  };

  const loadVocabularyTotal = () => {
    if (!langForeign.value || vocabularyTotalStates.value[langForeign.value] !== undefined) return;
    const lang = langForeign.value;
    VocabularyService.getVocabularyV1VocabularyGet({ query: { language: lang } }).then((res) => {
      vocabularyTotalStates.value[lang] = res.data?.words.length ?? 0;
    });
  };

  const getVocabularyTotal = (): number | undefined => {
    if (!langForeign.value) return undefined;
    return vocabularyTotalStates.value[langForeign.value];
  };

  const setLastJokeIndex = (index: number) => {
    if (!langForeign.value) return;
    jokeIndexStates.value[langForeign.value] = index;
    localStorage.setItem(LAST_JOKE_INDEX_KEY, JSON.stringify(jokeIndexStates.value));
  };

  const getLastJokeIndex = (): number | undefined => {
    if (!langForeign.value) return undefined;
    return jokeIndexStates.value[langForeign.value];
  };

  const initLastJokeIndexState = () => {
    const stored = localStorage.getItem(LAST_JOKE_INDEX_KEY);
    if (stored) {
      jokeIndexStates.value = JSON.parse(stored);
    }
  };

  const loadJokeTotal = () => {
    if (!langForeign.value || jokeTotalStates.value[langForeign.value] !== undefined) return;
    const lang = langForeign.value;
    JokesService.getJokesV1JokesGet({ query: { language: lang } }).then((res) => {
      jokeTotalStates.value[lang] = res.data?.jokes.length ?? 0;
    });
  };

  const getJokeTotal = (): number | undefined => {
    if (!langForeign.value) return undefined;
    return jokeTotalStates.value[langForeign.value];
  };

  return {
    state: {
      grammarMenu,
      grammarChapterStates,
      vocabularyIndexStates,
      vocabularyTotalStates,
      jokeIndexStates,
      jokeTotalStates,
      showGrammarDone,
      completedGrammarChapterNumber
    },
    funcs: {
      flattenChapters,
      getNextChapterNumber,
      getPreviousChapterNumber,
      findChapter,
      loadGrammarMenu,
      setLastGrammarChapter,
      getLastGrammarChapter,
      initLastGrammarChapterState,
      setLastVocabularyIndex,
      getLastVocabularyIndex,
      initLastVocabularyIndexState,
      loadVocabularyTotal,
      getVocabularyTotal,
      setLastJokeIndex,
      getLastJokeIndex,
      initLastJokeIndexState,
      loadJokeTotal,
      getJokeTotal
    },
    consts: {}
  };
};
